import { useEffect, useMemo, useRef, useState } from 'react';
import './YearlyPhotoCarousel2.css';

function YearlyPhotoCarousel2({ photos = [], autoplayInterval = 5000 }) {
  const safeInterval = Math.max(autoplayInterval, 1500);
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedSlides, setExpandedSlides] = useState({});
  const isMounted = useRef(false);
  const timerRef = useRef();
  const carouselRef = useRef(null);
  const yearsContainerRef = useRef(null);
  const yearButtonRefs = useRef([]);

  const slides = useMemo(
    () =>
      photos.map((photo) => ({
        ...photo,
        image: photo.image || photo.url || '',
        description: Array.isArray(photo.description) ? photo.description : [],
      })),
    [photos]
  );

  useEffect(() => {
    setActiveIndex(0);
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) {
      return undefined;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, safeInterval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [slides.length, safeInterval]);

  useEffect(() => {
    const container = yearsContainerRef.current;
    const currentButton = yearButtonRefs.current[activeIndex];
    if (!container || !currentButton) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const buttonRect = currentButton.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;
    const buttonCenter = buttonRect.left + buttonRect.width / 2;
    const delta = buttonCenter - containerCenter;

    if (Math.abs(delta) > 8) {
      const target = container.scrollLeft + delta;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const clamped = Math.max(0, Math.min(maxScroll, target));
      container.scrollTo({ left: clamped, behavior: 'smooth' });
    }
  }, [activeIndex]);

  const handleSelect = (index) => {
    setActiveIndex(index);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % slides.length);
      }, safeInterval);
    }
  };

  const handlePrev = () => {
    if (!slides.length) {
      return;
    }
    handleSelect((activeIndex - 1 + slides.length) % slides.length);
  };

  const handleNext = () => {
    if (!slides.length) {
      return;
    }
    handleSelect((activeIndex + 1) % slides.length);
  };

  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  };

  const handleMouseLeave = () => {
    if (slides.length <= 1 || timerRef.current) {
      return;
    }
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, safeInterval);
  };

  const toggleExpand = (year) => {
    setExpandedSlides((prev) => ({
      ...prev,
      [year]: !prev[year],
    }));
  };

  if (!slides.length) {
    return null;
  }

  return (
    <div className="yearly-carousel">
      <div className="yearly-carousel__controls">
        <button
          type="button"
          className="yearly-carousel__nav yearly-carousel__nav--prev"
          onClick={handlePrev}
          aria-label="上一張"
        >
          <span aria-hidden="true">❮</span>
        </button>

        <div
          ref={yearsContainerRef}
          className="yearly-carousel__years"
          role="tablist"
          aria-label="年度列表"
        >
          {slides.map((slide, index) => (
            <button
              key={slide.year}
              type="button"
              role="tab"
              ref={(el) => {
                yearButtonRefs.current[index] = el;
              }}
              className={`yearly-carousel__year ${index === activeIndex ? 'is-active' : ''}`}
              aria-selected={index === activeIndex}
              onClick={() => handleSelect(index)}
            >
              {slide.year}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="yearly-carousel__nav yearly-carousel__nav--next"
          onClick={handleNext}
          aria-label="下一張"
        >
          <span aria-hidden="true">❯</span>
        </button>
      </div>

      <div className="yearly-carousel__stage">
        <div
          className="yearly-carousel__frame-stack"
          ref={carouselRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div aria-hidden="true" className="yearly-carousel__frame yearly-carousel__frame--back" />
          <div aria-hidden="true" className="yearly-carousel__frame yearly-carousel__frame--mid" />

          <div className="yearly-carousel__slides">
            {slides.map((slide, index) => {
              const isExpanded = expandedSlides[slide.year];
              const displayedDesc = isExpanded ? slide.description : slide.description.slice(0, 1);

              return (
                <article
                  key={slide.year}
                  className={`yearly-carousel__slide ${index === activeIndex ? 'is-active' : ''}`}
                  aria-hidden={index !== activeIndex}
                >
                  <div className="yearly-carousel__image-wrapper">
                    <img src={slide.image} alt={slide.alt || ''} />

                    <div className={`yearly-carousel__overlay ${isExpanded ? 'is-expanded' : ''}`}>
                      <div className="yearly-carousel__overlay-header">
                        <p className="yearly-carousel__year-label">{slide.year} 年</p>
                        {slide.description.length > 1 && (
                          <button
                            type="button"
                            className="yearly-carousel__toggle"
                            onClick={() => toggleExpand(slide.year)}
                            aria-label={isExpanded ? '收起' : '展开'}
                          >
                            <span aria-hidden="true">{isExpanded ? '▼' : '▲'}</span>
                          </button>
                        )}
                      </div>

                      <div className="yearly-carousel__description">
                        {displayedDesc.map((item, descIndex) => (
                          <p key={item.slice(0, 24) + descIndex}>
                            <span className="yearly-carousel__bullet" aria-hidden="true">
                              {descIndex === 0 ? '⬆️' : '➡️'}
                            </span>
                            <span>{item}</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default YearlyPhotoCarousel2;
